import React, { ReactNode, useCallback, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { Backdrop } from './Backdrop';
import { BackdropContext, BackdropContextValue, BackdropShowOptions } from './BackdropContext';

export interface BackdropContextProviderState {
  backdrops: Array<{
    id: string;
    loading: boolean;
    transitionDuration: number;
    backgroundColor?: string;
    zIndex: number;
    onClick?: () => void;
  }>;
}

interface BackdropContextProviderProps {
  children: ReactNode;
}

export const BackdropContextProvider: React.FC<BackdropContextProviderProps> = ({ children }) => {
  const [state, setState] = React.useState<BackdropContextProviderState>({
    backdrops: [],
  });

  const activeBackdrop = state.backdrops[state.backdrops.length - 1];

  const show = useCallback(
    ({ loading = false, transitionDuration = 300, backgroundColor, zIndex, onClick }: BackdropShowOptions) => {
      const newBackdrop = {
        id: uuidv4(),
        loading,
        transitionDuration,
        backgroundColor,
        zIndex,
        onClick,
      };

      setState(st => ({
        backdrops: [...st.backdrops, newBackdrop],
      }));

      return newBackdrop.id;
    },
    [],
  );

  const hide = useCallback((backdropId: string) => {
    setState(st => ({
      backdrops: st.backdrops.filter(d => d.id !== backdropId),
    }));
  }, []);

  const ctxValue: BackdropContextValue = useMemo(
    () => ({
      initialized: true,
      activeBackdrop,
      show,
      hide,
    }),
    [activeBackdrop, hide, show],
  );

  return (
    <BackdropContext.Provider value={ctxValue}>
      <Backdrop
        active={!!activeBackdrop}
        loading={activeBackdrop ? activeBackdrop.loading : false}
        transitionDuration={activeBackdrop ? activeBackdrop.transitionDuration : 0}
        backgroundColor={activeBackdrop ? activeBackdrop.backgroundColor : 'transparent'}
        zIndex={activeBackdrop ? activeBackdrop.zIndex : 0}
        onClick={activeBackdrop ? activeBackdrop.onClick : undefined}
      />
      {children}
    </BackdropContext.Provider>
  );
};
